import {Injectable} from '@angular/core';
import { MarkupEnumType } from 'src/app/shared/enums/estimate/estimate.enum';
import {calculatedCostlineValuesDTO, EstimateCostLineItemsEntityDTO} from 'src/app/shared/modals/estimate.modal';
import {decimal2, NumberUtil} from 'src/app/shared/utils/util-function';

type ServerPricedCostLine = EstimateCostLineItemsEntityDTO & { CustomerPrice: number };

@Injectable({
  providedIn: 'root'
})
export class CostlineServiceService {

  constructor() {
  }

  calculateCostlineValues(companyCostPer: number, qty: number, markup: number, markupType: string, isTaxable: boolean, isTaxCompanyCostOnly: boolean,isMargin:boolean): calculatedCostlineValuesDTO {
    companyCostPer = companyCostPer;
    qty = NumberUtil.ToNumber(qty, 6);
    markup = NumberUtil.ToNumber(markup,4);

    let calculatedCompanyCost = NumberUtil.Multiply(qty, companyCostPer);
    let calculatedCustomerCost = this.calculateClientPriceByMarkupMargin(markupType, markup, calculatedCompanyCost, qty,isMargin);


    let calculatedMarkup = calculatedCustomerCost - calculatedCompanyCost; // For both cases Markup & Margin 
    let taxableAmt = this.calculateTaxableAmt(calculatedCustomerCost, companyCostPer * (qty || 0), isTaxable, isTaxCompanyCostOnly);

    return {
      calculatedCustomerCost,
      calculatedMarkup,
      calculatedCompanyCost,
      taxableAmt
    } as calculatedCostlineValuesDTO;

  }
  calculateClientPriceByMarkupMargin(markupType: string, markupOrMarginValue: number, calculatedCompanyCost: number, qty: number, isMargin: boolean) {
    calculatedCompanyCost = calculatedCompanyCost || 0;
    markupOrMarginValue = markupOrMarginValue || 0;

    if(isMargin)
    {
      let calculatePrice = markupType === MarkupEnumType.Percentage ? NumberUtil.roundNumber((calculatedCompanyCost / (1-(markupOrMarginValue/100))),true) : ((markupOrMarginValue*qty) + calculatedCompanyCost);
      return decimal2(calculatePrice || 0);
    }
    else
    {
      let calculatePrice = markupType === MarkupEnumType.Percentage ? (calculatedCompanyCost + NumberUtil.CalculatePercentAmount(calculatedCompanyCost, markupOrMarginValue)) : ((markupOrMarginValue*qty) + calculatedCompanyCost);
      return decimal2(calculatePrice || 0);
    }
  }

  calculateCostlineTax(costLine: EstimateCostLineItemsEntityDTO, taxPercent: number) {
    if (!costLine.IsTaxable)
      return 0;

    return NumberUtil.CalculatePercentAmount(this.costlineTaxableAmt(costLine), taxPercent);
  }

  costlineTaxableAmt(costLine: EstimateCostLineItemsEntityDTO) {
    return this.calculateTaxableAmt(costLine.Price, this.taxableCompanyCost(costLine), costLine.IsTaxable, costLine.IsTaxCompanyCostOnly);
  }

  /** `companyCost` is the line's company cost times its quantity, what a tax-on-cost line is taxed on. */
  calculateTaxableAmt(price: number, companyCost: number, isTaxable: boolean, applyTaxOnCompanyCost: boolean) {

    if (!isTaxable)
      return 0;

    return applyTaxOnCompanyCost ? companyCost : price;
  }

  /**
   * A share link that withholds a line's company cost and markup states its price and tax base instead
   * (ProjulV2Backend PortalCostLineMarkup.ForCustomerPortal); without them the line is priced from its cost.
   */
  private isServerPriced(costLine: EstimateCostLineItemsEntityDTO): costLine is ServerPricedCostLine {
    return typeof costLine.CustomerPrice === 'number';
  }

  private taxableCompanyCost(costLine: EstimateCostLineItemsEntityDTO): number {
    return this.isServerPriced(costLine) ? (costLine.TaxableCompanyCost || 0) : costLine.DefaultCostPer * (costLine.DefaultQty || 0);
  }


  //This will calculate the markup amount we need to add on the total amount
  private calculateMarkup = (markupType: string, markup: number, priceOrCost: number, qty: number) => {
    return markupType === '%' ? NumberUtil.CalculatePercentAmount(markup, priceOrCost,false) : (markup * qty);
  }

  //This will calculate the minimum markup on company cost
  private calculateMinimumMarkup = (markupType: string, markup: number, companyCost: number) => {
    return markupType === '%' ? markup : NumberUtil.CalculatePercentAmount(markup, companyCost);
  }

   setDerivedProps(costLine: EstimateCostLineItemsEntityDTO) {
    if (this.isServerPriced(costLine)) {
      this.setServerPricedProps(costLine);
      return;
    }

    this.setMarkupOrMargin(costLine); //Note: We are not setting IsMargin value because in customer portal we are not showing markup/margin any where. But if in future we have requirement to show markup/margin then we need to ask from client which user's setting based we will show margin/markup. 
    let calculatedValues = this.calculateCostlineValues(costLine.DefaultCostPer, costLine.DefaultQty, costLine.DefaultMarkupPer, costLine.DefaultMarkupType,costLine.IsTaxable,costLine.IsTaxCompanyCostOnly,costLine.IsMargin);
    costLine.calculatedMarkup = calculatedValues.calculatedMarkup;
    costLine.calculatedCompanyCost = calculatedValues.calculatedCompanyCost;
    costLine.calculatedCustomerCost = calculatedValues.calculatedCustomerCost;
    costLine.calculatedMinimumMarkup = calculatedValues.calculatedMinimumMarkup;
    costLine.taxableAmt = calculatedValues.taxableAmt;

    if (!costLine.Price)
    costLine.Price = calculatedValues.calculatedCustomerCost;
  }

  /** The values the cost path derives, read from the figures the share link states in place of the cost. */
  private setServerPricedProps(costLine: ServerPricedCostLine) {
    costLine.Price = costLine.Price || 0;
    costLine.calculatedCustomerCost = costLine.CustomerPrice;
    costLine.calculatedCompanyCost = this.taxableCompanyCost(costLine);
    costLine.calculatedMarkup = costLine.calculatedCustomerCost - costLine.calculatedCompanyCost;
    costLine.taxableAmt = this.calculateTaxableAmt(costLine.calculatedCustomerCost, costLine.calculatedCompanyCost, costLine.IsTaxable, costLine.IsTaxCompanyCostOnly);

    if (!costLine.Price)
      costLine.Price = costLine.calculatedCustomerCost;
  }

  setMarkupOrMargin(costline:EstimateCostLineItemsEntityDTO) //It will set the DefaultMarkupPer value against costline based on Markup/Margin Switch 
  {  
      costline.Price = (costline.Price || 0);
      costline.DefaultCostPer = (costline.DefaultCostPer || 0);
      costline.DefaultMarkupPer = (costline.DefaultMarkupPer || 0);

      let Quantity = (costline.DefaultQty || 0);

      let calculatedCompanyCost = NumberUtil.Multiply(Quantity, costline.DefaultCostPer);
 
      if(((costline.DefaultQty || 0) > 0) && ((calculatedCompanyCost || 0) !== 0 || (costline.Price || 0) !== 0))
      {
        if(costline.IsMargin && (costline.Price || 0) <= 0 && (calculatedCompanyCost || 0) !== 0) // If costline is in margin with some cost, If price is set to zero or negative then set markup type to dollar
        {
          costline.DefaultMarkupType = MarkupEnumType.Amount; // Set Markup Type to Dollar
        }

        if((calculatedCompanyCost || 0) == 0 && (costline.Price || 0) !== 0)
        {
          costline.DefaultMarkupPer = costline.Price/Quantity;
          costline.DefaultMarkupType = MarkupEnumType.Amount; // Set Markup Type to Dollar
        }
        else if(costline.Price == 0 && (calculatedCompanyCost || 0) !== 0)
        {
          if(costline.DefaultMarkupType == MarkupEnumType.Amount)
          {
            costline.DefaultMarkupPer = -calculatedCompanyCost/Quantity;
          }
          else
          {
            if(!costline.IsMargin && calculatedCompanyCost > 0)
            {
              costline.DefaultMarkupPer = -100;
            }
            else
            {
              costline.DefaultMarkupPer = 0;
            }
          }
        }
        else
        {
          if(costline.DefaultMarkupType == MarkupEnumType.Percentage) 
          {
            if(costline.IsMargin) // If Margin
            {
              costline.DefaultMarkupPer = ((costline.Price - calculatedCompanyCost) / costline.Price) * 100; //(Item price - Item cost) / Item price => (-300 - 200) / -300 = -2.5 = -250%
            }
            else // If markup
            {
              costline.DefaultMarkupPer = ((costline.Price - calculatedCompanyCost) / calculatedCompanyCost) * 100; //(Item price - Item cost) / Item cost => (-300 - 200) / 200 = -2.5 = -250%
            }
          }
          else
          {
            costline.DefaultMarkupPer = (costline.Price - calculatedCompanyCost)/Quantity; //(Item price - Item cost) => (-300 - 200) = -500
          }

          costline.DefaultMarkupPer = decimal2(costline.DefaultMarkupPer,false,4); 
          // if(changeByMarkupOrMarginFields && !costline.IsMargin)
          // costline.DefaultMarkupPer = decimal2(costline.DefaultMarkupPer,true); 
        }
      }
      else
      {
        if(costline.IsMargin && costline.DefaultMarkupType == MarkupEnumType.Percentage)
        {
          if(costline.DefaultMarkupPer > 99)
          {
            costline.DefaultMarkupPer = 99;
          }
        }
      }
  }


}
